import { CloudinaryUploader } from "@/components/CloudinaryUploader";
import { SecondaryButton } from "./AdminUI";
import { ArrowDown, ArrowUp, ImagePlus, X } from "lucide-react";

/**
 * Editable gallery for a clothing item's "Editor's images" — photos uploaded
 * directly via the clothing edit form. Each entry is `{url}`. Uploads use the
 * `personal_images` Cloudinary folder (same as the brand "Our take" gallery).
 *
 * Props:
 *  - value: [{url}]
 *  - onChange: (next) => void
 */
export function EditorImagesField({ value, onChange }) {
  const items = value || [];

  function update(idx, patch) {
    onChange(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  }
  function remove(idx) {
    onChange(items.filter((_, i) => i !== idx));
  }
  function move(idx, delta) {
    const next = items.slice();
    const target = idx + delta;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  }
  function addByUrl() {
    const url = window.prompt("Image URL");
    if (url && url.startsWith("http")) onChange([...items, { url }]);
  }
  function addUploaded(url) {
    onChange([...items, { url }]);
  }

  return (
    <div data-testid="editor-images-field">
      <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
        <h3 className="text-xs uppercase tracking-wider text-[#6C757D]">Editor&apos;s images (optional)</h3>
        <div className="flex items-center gap-2">
          <CloudinaryUploader
            mode="admin"
            folder="sustainably_curated/personal_images"
            multiple={true}
            label="Upload"
            onUploaded={addUploaded}
          />
          <SecondaryButton onClick={addByUrl} data-testid="editor-image-add-url">
            <ImagePlus className="w-3.5 h-3.5 inline mr-1" /> Add via URL
          </SecondaryButton>
        </div>
      </div>

      {items.length === 0 ? (
        <p className="text-xs text-[#6C757D] italic">No images yet. Upload your own photos of this clothing item to show in the public &quot;Editor&apos;s images&quot; section.</p>
      ) : (
        <div className="space-y-2">
          {items.map((it, i) => (
            <div key={i} className="flex items-start gap-2 border border-[#E9ECEF] rounded-md p-2 bg-white" data-testid={`editor-image-${i}`}>
              <div className="w-20 h-20 shrink-0 bg-[#F1F3F5] rounded overflow-hidden flex items-center justify-center">
                {it.url ? (
                  <img src={it.url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <ImagePlus className="w-5 h-5 text-[#6C757D]" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <input
                  className="w-full text-xs border border-[#E9ECEF] rounded px-2 py-1 font-mono"
                  value={it.url || ""}
                  onChange={(e) => update(i, { url: e.target.value })}
                  placeholder="Image URL"
                  data-testid={`editor-image-url-${i}`}
                />
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-xs p-1 text-[#6C757D] disabled:opacity-30" title="Move up"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-xs p-1 text-[#6C757D] disabled:opacity-30" title="Move down"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => remove(i)} className="text-xs p-1 text-clay" title="Remove"><X className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
